'use client';

import { SortOrderEnum } from '@/lib/types';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useSnackbar } from 'notistack';
import type { GridSortModel } from '@mui/x-data-grid-pro';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import useSearchStore from '@/publicSearch/store/useSearchStore';
import useGridStore from '@/publicSearch/store/useGridStore';
import { getFilterStore } from '@/publicSearch/store/useFilterStore';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import { logError } from '@/lib/logger';
import { LogicalOperator } from '@/components/publicSearch/constants';
import type { FilterGroup } from '@/components/publicSearch/constants';
import { SearchType, useSearchType } from './useSearchType';
import { SharedSearchResult } from '@/publicSearch/graphql/sharedSearch';
import { SNACKBAR_VARIANT } from '@/constants/notifications';

const SHARED_SEARCH_QUERY = `
  query GetSharedSearch($shortCode: String!, $societyId: Int!) {
    sharedSearch(shortCode: $shortCode, societyId: $societyId) {
      shortCode
      searchType
      filters { field operator value }
      commonFilterOperator
      searchTerm
      gridSortModel { field sort }
      isAiMode
      filterGroups {
        logicalOperator
        filters { field operator value }
      }
      groupsOperator
      aiSearchPrompt
    }
  }
`;

function mapGroupsOperator(s: string | undefined): LogicalOperator {
  return s === LogicalOperator.and ? LogicalOperator.and : LogicalOperator.or;
}

function mapGroupLogicalOp(s: string | undefined): LogicalOperator {
  return s === LogicalOperator.or ? LogicalOperator.or : LogicalOperator.and;
}

async function fetchSharedSearchDirect(
  shortCode: string,
  societyId: number,
  orgId: string | number,
): Promise<SharedSearchResult | null> {
  const res = await fetch('/api/publicSearch/graphql', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...setDefaultHeader(orgId),
    },
    body: JSON.stringify({
      operationName: 'GetSharedSearch',
      query: SHARED_SEARCH_QUERY,
      variables: { shortCode, societyId },
    }),
  });

  if (!res.ok) return null;
  const json = await res.json();
  return json?.data?.sharedSearch ?? null;
}

export function useSharedSearchHydration() {
  const params = useSearchParams();
  const shortCode = params?.get('shared');
  const orgId = usePublicSearchContext((ctx) => ctx.society.orgId);
  const searchType = useSearchType();
  const { enqueueSnackbar } = useSnackbar();
  const hydratedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!shortCode || !orgId) return;
    if (hydratedRef.current === shortCode) return;
    hydratedRef.current = shortCode;

    const hydrate = async () => {
      try {
        const result = await fetchSharedSearchDirect(
          shortCode,
          Number(orgId),
          orgId,
        );

        if (!result) {
          enqueueSnackbar('Shared search link not found or has expired', {
            variant: SNACKBAR_VARIANT.WARNING,
            autoHideDuration: 3000,
          });
          return;
        }

        const targetSearchType = (result.searchType ||
          searchType) as SearchType;
        const store = getFilterStore(targetSearchType);

        const flatFilters = Array.isArray(result.filters) ? result.filters : [];
        const mappedFlat = flatFilters.map((f) => ({
          field: f.field ?? '',
          operator: f.operator ?? '',
          value: f.value ?? '',
        }));

        const hasGrouped =
          Array.isArray(result.filterGroups) &&
          result.filterGroups.some((g) => (g.filters?.length ?? 0) > 0);

        store.getState().clearFilterGroups();

        if (hasGrouped) {
          const mappedGroups: FilterGroup[] = result.filterGroups.map((g) => ({
            logicalOperator: mapGroupLogicalOp(g.logicalOperator),
            filters: (g.filters ?? []).map((f) => ({
              field: f.field ?? '',
              operator: f.operator ?? '',
              value: f.value ?? '',
            })),
          }));
          store.getState().setFilterGroups(mappedGroups);
          store
            .getState()
            .setGroupsOperator(mapGroupsOperator(result.groupsOperator));
        }

        store.getState().setFilters(mappedFlat);

        const operator =
          result.commonFilterOperator === LogicalOperator.and
            ? LogicalOperator.and
            : LogicalOperator.or;
        store.getState().updateCommonFilterOperator(operator);

        if (hasGrouped) {
          useSearchStore.getState().setIsAiMode(false);
          useSearchStore.getState().setAiSearchPrompt('');
          useSearchStore
            .getState()
            .setSearchDisplayTerm(result.searchTerm ?? '');
        } else {
          const isAi = result.isAiMode ?? false;
          const prompt = (result.aiSearchPrompt ?? '').trim();
          useSearchStore.getState().setIsAiMode(isAi);
          useSearchStore.getState().setAiSearchPrompt(isAi ? prompt : '');
          const display = isAi && prompt ? prompt : (result.searchTerm ?? '');
          useSearchStore.getState().setSearchDisplayTerm(display);
        }

        const sortItems = Array.isArray(result.gridSortModel)
          ? result.gridSortModel
          : [];
        const sortModel: GridSortModel =
          sortItems.length > 0
            ? sortItems.map((s) => {
                const resolveSort = (): SortOrderEnum | null => {
                  if (
                    s.sort === SortOrderEnum.ASC ||
                    s.sort === SortOrderEnum.DESC
                  ) {
                    return s.sort;
                  }
                  return s.sort ? (s.sort as SortOrderEnum) : null;
                };
                return {
                  field: s.field,
                  sort: resolveSort(),
                };
              })
            : [];
        useGridStore.getState().setGridSortModel(sortModel);

        useSearchStore.getState().setOpenResult(true);
      } catch (err) {
        logError('[SharedSearch] hydration failed', err);
        enqueueSnackbar('Failed to load shared search', {
          variant: SNACKBAR_VARIANT.ERROR,
          autoHideDuration: 3000,
        });
      }
    };

    void hydrate();
  }, [shortCode, orgId, searchType, enqueueSnackbar]);
}
